import { c as _c } from "react/compiler-runtime";
import { Dropdown, DropdownSubmenu, IconButton, openDialog, Tooltip, useTranslationContext } from 'platform/components';
import { useMemo } from 'react';
import { isNotNil, noop } from 'ramda-adjunct';
import { suffixTestId } from 'shared/web';
import { Preset } from '../../types/Api';
import { EditPresetDialog } from './components/EditPresetDialog/EditPresetDialog';
import { PresetButton } from './components/PresetButton/PresetButton';
import { PresetButtonActions } from './components/PresetButtonActions/PresetButtonActions';
import { PresetApiStateEnum } from './constants/PresetApiStateEnum';
import { usePresetApiState } from './hooks/usePresetApiState';
import { usePresetOverflow } from './hooks/usePresetOverflow';
import { usePresetRowCounts } from './hooks/usePresetRowCounts';
import { PresetsPanelRendererProps } from './types/PresetsPanelRendererType';
const PRESET_WRAPPER_HEIGHT = 36;
export function PresetsPanelRenderer(props) {
  const $ = _c(85);
  const t = useTranslationContext();
  let t0;
  if ($[0] !== props.presets) {
    t0 = {
      presets: props.presets
    };
    $[0] = props.presets;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    visibleCount,
    wrapperWidth,
    buttonsWrapperRef,
    buttonsGhostRef
  } = usePresetOverflow(t0);
  let t1;
  if ($[2] !== props.deletePreset || $[3] !== props.duplicatePreset || $[4] !== props.updatePreset || $[5] !== props.updatePresetPosition) {
    t1 = {
      updatePreset: props.updatePreset,
      duplicatePreset: props.duplicatePreset,
      deletePreset: props.deletePreset,
      updatePresetPosition: props.updatePresetPosition
    };
    $[2] = props.deletePreset;
    $[3] = props.duplicatePreset;
    $[4] = props.updatePreset;
    $[5] = props.updatePresetPosition;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    apiState,
    isPresetChangeDisabled,
    disabledPresetChange,
    allowPresetChange,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    updatePresetPositionProxy
  } = usePresetApiState(t1);
  const activePresetId = (props.activePreset as Preset)?.id;
  let t2;
  bb0: {
    if (!props.presets || visibleCount === undefined) {
      let t3;
      if ($[7] !== props.presets || $[8] !== visibleCount) {
        t3 = props.presets?.slice(0, visibleCount);
        $[7] = props.presets;
        $[8] = visibleCount;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      let t4;
      if ($[10] !== t3) {
        t4 = {
          visibleItems: t3,
          hiddenItems: undefined
        };
        $[10] = t3;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      t2 = t4;
      break bb0;
    }
    const activeIndex = props.presets.findIndex(p => p.id === activePresetId);
    const activeIsHidden = activeIndex >= visibleCount;
    if (!activeIsHidden) {
      let t3;
      if ($[12] !== props.presets || $[13] !== visibleCount) {
        t3 = props.presets.slice(0, visibleCount);
        $[12] = props.presets;
        $[13] = visibleCount;
        $[14] = t3;
      } else {
        t3 = $[14];
      }
      let t4;
      if ($[15] !== props.presets || $[16] !== visibleCount) {
        t4 = props.presets.slice(visibleCount);
        $[15] = props.presets;
        $[16] = visibleCount;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      let t5;
      if ($[18] !== t3 || $[19] !== t4) {
        t5 = {
          visibleItems: t3,
          hiddenItems: t4
        };
        $[18] = t3;
        $[19] = t4;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      t2 = t5;
      break bb0;
    }
    const active = props.presets[activeIndex];
    let t3;
    if ($[21] !== active || $[22] !== activePresetId || $[23] !== props.presets || $[24] !== visibleCount) {
      const originalVisible = props.presets.slice(0, visibleCount);
      let t4;
      if ($[26] !== activePresetId) {
        t4 = p_0 => p_0.id !== activePresetId;
        $[26] = activePresetId;
        $[27] = t4;
      } else {
        t4 = $[27];
      }
      const originalHidden = props.presets.slice(visibleCount).filter(t4);
      t3 = {
        visibleItems: [active, ...originalVisible.slice(0, visibleCount - 1)],
        hiddenItems: [originalVisible[visibleCount - 1], ...originalHidden]
      };
      $[21] = active;
      $[22] = activePresetId;
      $[23] = props.presets;
      $[24] = visibleCount;
      $[25] = t3;
    } else {
      t3 = $[25];
    }
    t2 = t3;
  }
  const {
    visibleItems,
    hiddenItems
  } = t2;
  let t3;
  if ($[28] !== props.rowCountGetter || $[29] !== visibleItems) {
    t3 = {
      visibleItems,
      rowCountGetter: props.rowCountGetter
    };
    $[28] = props.rowCountGetter;
    $[29] = visibleItems;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  const rowCounts = usePresetRowCounts(t3);
  const presetsRowCount = rowCounts.presetsRowCount;
  let t4;
  if ($[31] !== t) {
    t4 = t("page.actions.createPreset");
    $[31] = t;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== props || $[34] !== t) {
    t5 = () => openDialog(<EditPresetDialog onSubmit={props.createPreset} data-testid={suffixTestId("createPreset", props)} />, {
      id: "createPreset",
      title: t("general.presets.createPreset"),
      "data-testid": suffixTestId("createPreset", props),
      size: "small",
      scrollBehavior: "outside"
    });
    $[33] = props;
    $[34] = t;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== props) {
    t6 = suffixTestId("createPresetButton", props);
    $[36] = props;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== t5 || $[39] !== t6) {
    t7 = <div className="rounded-full overflow-hidden bg-gray-100"><IconButton onClick={t5} data-testid={t6} icon="content/add" /></div>;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  let t8;
  if ($[41] !== t4 || $[42] !== t7) {
    t8 = <Tooltip label={t4} placement="top">{t7}</Tooltip>;
    $[41] = t4;
    $[42] = t7;
    $[43] = t8;
  } else {
    t8 = $[43];
  }
  let t9;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      maxHeight: PRESET_WRAPPER_HEIGHT
    };
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== allowPresetChange || $[46] !== apiState || $[47] !== deletePresetProxy || $[48] !== disabledPresetChange || $[49] !== duplicatePresetProxy || $[50] !== isPresetChangeDisabled || $[51] !== presetsRowCount || $[52] !== props || $[53] !== t || $[54] !== updatePresetPositionProxy || $[55] !== updatePresetProxy || $[56] !== visibleItems) {
    t10 = visibleItems?.map(preset => {
      const isActive = preset.id === (props.activePreset as Preset).id;
      const state = isNotNil(apiState[preset.id]) ? apiState[preset.id] : PresetApiStateEnum.idle;
      const globalIndex = props.presets?.findIndex(p_1 => p_1.id === preset.id) ?? -1;
      return <PresetButton rowCount={presetsRowCount[preset.dataQueryId]} unsaved={props.isPresetChanged && isActive} key={preset.id} preset={preset} state={state} active={isActive} isDisabled={isPresetChangeDisabled} onClick={() => {
        disabledPresetChange();
        const operation = isActive ? props.deactivatePreset() : props.activatePreset(preset);
        operation.finally(allowPresetChange);
      }} data-testid={suffixTestId(`preset-${preset.title}`, props)} actions={<PresetButtonActions preset={preset} index={globalIndex} presetChanged={props.isPresetChanged} activePresetId={(props.activePreset as Preset)?.id} updatePreset={updatePresetProxy} deletePreset={deletePresetProxy} duplicatePreset={duplicatePresetProxy} resetOverrides={props.resetOverrides} updatePresetPosition={updatePresetPositionProxy} data-testid={suffixTestId(`preset-${preset.title}`, props)} openEditModal={() => openDialog(<EditPresetDialog presetToEdit={preset} data-testid={suffixTestId("editPreset", props)} onSubmit={(title, description) => props.renamePreset(preset.id, title, description)} />, {
        id: "editPreset",
        title: t("general.presets.editPreset", {
          presetName: preset.title
        }),
        "data-testid": suffixTestId("editPreset", props),
        size: "small",
        scrollBehavior: "outside"
      })} presetsLength={props.presets?.length ?? 0} />} />;
    });
    $[45] = allowPresetChange;
    $[46] = apiState;
    $[47] = deletePresetProxy;
    $[48] = disabledPresetChange;
    $[49] = duplicatePresetProxy;
    $[50] = isPresetChangeDisabled;
    $[51] = presetsRowCount;
    $[52] = props;
    $[53] = t;
    $[54] = updatePresetPositionProxy;
    $[55] = updatePresetProxy;
    $[56] = visibleItems;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  let t11;
  if ($[58] !== deletePresetProxy || $[59] !== duplicatePresetProxy || $[60] !== hiddenItems || $[61] !== props || $[62] !== t || $[63] !== updatePresetPositionProxy || $[64] !== updatePresetProxy) {
    t11 = isNotNil(hiddenItems?.length) && (hiddenItems?.length ?? 0) > 0 && <Dropdown isLazy={true} strategy="fixed" dropdownControl={<IconButton icon="navigation/more_vert" data-testid="button-preset.more" />} data-testid={suffixTestId("dropDownPresetMore", props)}>{hiddenItems?.map(preset_0 => {
        const globalIndex_0 = props.presets?.findIndex(p_2 => p_2.id === preset_0.id) ?? -1;
        const isActive_0 = preset_0.id === (props.activePreset as Preset).id;
        return <DropdownSubmenu isLazy={true} isActive={isActive_0} key={preset_0.id} onClick={() => {
          if (isActive_0) {
            props.deactivatePreset();
          } else {
            props.activatePreset(preset_0);
          }
        }} label={preset_0.title} data-id={`menu-preset.${preset_0.title}`}><PresetButtonActions preset={preset_0} index={globalIndex_0} presetChanged={props.isPresetChanged} activePresetId={(props.activePreset as Preset)?.id} updatePreset={updatePresetProxy} deletePreset={deletePresetProxy} duplicatePreset={duplicatePresetProxy} resetOverrides={props.resetOverrides} updatePresetPosition={updatePresetPositionProxy} data-testid={`menu-preset.${preset_0.title}`} openEditModal={() => openDialog(<EditPresetDialog presetToEdit={preset_0} data-testid="button-createPreset" onSubmit={(title_0, description_0) => props.renamePreset(preset_0.id, title_0, description_0)} />, {
            title: t("general.presets.editPreset", {
              presetName: preset_0.title
            }),
            "data-testid": "editPreset",
            size: "small",
            scrollBehavior: "outside"
          })} presetsLength={props.presets?.length ?? 0} /></DropdownSubmenu>;
      })}</Dropdown>;
    $[58] = deletePresetProxy;
    $[59] = duplicatePresetProxy;
    $[60] = hiddenItems;
    $[61] = props;
    $[62] = t;
    $[63] = updatePresetPositionProxy;
    $[64] = updatePresetProxy;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== t11) {
    t12 = <div className="inline-flex">{t11}</div>;
    $[66] = t11;
    $[67] = t12;
  } else {
    t12 = $[67];
  }
  let t13;
  if ($[68] !== buttonsWrapperRef || $[69] !== t10 || $[70] !== t12) {
    t13 = <div ref={buttonsWrapperRef} style={t9} className="flex flex-nowrap overflow-hidden w-full whitespace-nowrap flex-shrink gap-x-2">{t10}{t12}</div>;
    $[68] = buttonsWrapperRef;
    $[69] = t10;
    $[70] = t12;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  let t14;
  if ($[72] !== wrapperWidth) {
    t14 = {
      position: "absolute",
      zIndex: -999,
      opacity: 0,
      visibility: "hidden",
      width: wrapperWidth
    };
    $[72] = wrapperWidth;
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  let t15;
  if ($[74] !== presetsRowCount || $[75] !== props.presets) {
    t15 = props.presets?.map(preset_1 => <PresetButton rowCount={presetsRowCount[preset_1.dataQueryId]} unsaved={false} key={`ghost-${preset_1.id}`} preset={preset_1} state={PresetApiStateEnum.idle} active={false} onClick={noop} data-id={`menu-preset.${preset_1.title}`} />);
    $[74] = presetsRowCount;
    $[75] = props.presets;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  let t16;
  if ($[77] !== buttonsGhostRef || $[78] !== t14 || $[79] !== t15) {
    t16 = <div ref={buttonsGhostRef} className="pointer-events-none flex flex-wrap overflow-hidden w-full whitespace-nowrap flex-shrink gap-x-2" style={t14}>{t15}</div>;
    $[77] = buttonsGhostRef;
    $[78] = t14;
    $[79] = t15;
    $[80] = t16;
  } else {
    t16 = $[80];
  }
  let t17;
  if ($[81] !== t13 || $[82] !== t16 || $[83] !== t8) {
    t17 = <div className="relative gap-2 flex-grow z-(--z-content) overflow-hidden inline-flex items-center">{t8}{t13}{t16}</div>;
    $[81] = t13;
    $[82] = t16;
    $[83] = t8;
    $[84] = t17;
  } else {
    t17 = $[84];
  }
  return t17;
}